import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy } from 'lucide-react';

// Extracted motion constants to avoid recreating objects on every render (Phase 5.4).
const BAR_INITIAL = { width: 0 };
const BAR_TRANSITION = { duration: 0.5 };

function VoteBar({ name, votes, pct, winner }) {
  const animate = useMemo(() => ({ width: `${pct || 0}%` }), [pct]);
  return (
    <div className={`rounded-lg border p-4 ${winner ? 'border-[color:hsla(42,92%,52%,0.5)] bg-[color:hsla(42,92%,52%,0.06)]' : 'border-border bg-[var(--g66-surface)]'}`}>
      <div className="text-sm text-muted-foreground">{name}</div>
      <div className="flex items-baseline justify-between mt-1">
        <div className="font-display text-3xl text-white">{votes || 0}</div>
        <div className="text-lg text-[var(--g66-amber)]">{pct?.toFixed?.(1) || 0}%</div>
      </div>
      <div className="h-2 rounded-full bg-[#0b0c0f] overflow-hidden border border-border mt-2">
        <motion.div
          initial={BAR_INITIAL}
          animate={animate}
          transition={BAR_TRANSITION}
          className={`h-full ${winner ? 'bg-[var(--g66-amber)]' : 'bg-[var(--g66-red)]'}`}
        />
      </div>
    </div>
  );
}

/**
 * Result card shown when the stage is finished. Encapsulates the winner banner,
 * per-band vote bars (2 or 3), and the tiebreaker method note.
 */
export function StageResultCard({ stage }) {
  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-5">
        <div className="font-display text-2xl text-white mb-3">RESULTADO</div>
        <div className={`grid grid-cols-1 gap-3 ${stage.bands_count === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <VoteBar
            name={stage.band_a?.name}
            votes={stage.band_a_votes}
            pct={stage.band_a_percentage}
            winner={stage.winner_band_id === stage.band_a_id}
          />
          <VoteBar
            name={stage.band_b?.name}
            votes={stage.band_b_votes}
            pct={stage.band_b_percentage}
            winner={stage.winner_band_id === stage.band_b_id}
          />
          {stage.bands_count === 3 && stage.band_c && (
            <VoteBar
              name={stage.band_c?.name}
              votes={stage.band_c_votes}
              pct={stage.band_c_percentage}
              winner={stage.winner_band_id === stage.band_c_id}
            />
          )}
        </div>
        {stage.winner_band && (
          <div className="mt-4 flex items-center gap-2 text-[var(--g66-amber)]">
            <Trophy className="w-5 h-5" />
            <div className="font-display text-2xl">Vencedora: {stage.winner_band?.name}</div>
          </div>
        )}
        {stage.tiebreaker_method && (
          <div className="text-xs text-muted-foreground mt-2">
            Desempate: {stage.tiebreaker_method}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
